import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FloorPlanTable, { getTableScale } from './FloorPlanTable';
import './FloorPlanCanvas.css';

const GROUP_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981'];

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.15;

function FloorPlanCanvas({
  tables,
  onTableDragEnd,
  onCanvasClick,
  onTableContextMenu,
  placingTable,
  groups,
  selectedTableNumbers,
  selectionMode,
  onTableSelect,
  onGroupClick,
}) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const viewportRef = useRef(null);
  const [ghostPos, setGhostPos] = useState(null);

  // Zoom/pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const panOrigin = useRef({ x: 0, y: 0 });

  // Convert screen coordinates to viewport percentage coordinates
  const screenToViewportPct = useCallback((clientX, clientY) => {
    const vp = viewportRef.current;
    if (!vp) return { x: 50, y: 50 };
    const rect = vp.getBoundingClientRect();
    let pctX = ((clientX - rect.left) / rect.width) * 100;
    let pctY = ((clientY - rect.top) / rect.height) * 100;
    pctX = Math.max(2, Math.min(98, pctX));
    pctY = Math.max(2, Math.min(98, pctY));
    return { x: pctX, y: pctY };
  }, []);

  const handleCanvasClick = useCallback(
    (e) => {
      // Don't place if we were panning
      if (isPanning.current) return;

      const isCanvas = e.target === canvasRef.current || e.target === viewportRef.current;
      const isGhost = e.target.closest('.fp-ghost');
      if (!isCanvas && !isGhost) return;
      if (!placingTable) return;

      const { x: pctX, y: pctY } = screenToViewportPct(e.clientX, e.clientY);
      setGhostPos(null);
      onCanvasClick(pctX, pctY);
    },
    [placingTable, onCanvasClick, screenToViewportPct]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!placingTable) return;

      const { x: pctX, y: pctY } = screenToViewportPct(e.clientX, e.clientY);
      setGhostPos({ x: pctX, y: pctY });
    },
    [placingTable, screenToViewportPct]
  );

  const handleMouseLeave = useCallback(() => {
    setGhostPos(null);
  }, []);

  // Wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    // Mouse position relative to canvas container
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    setZoom((prevZoom) => {
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prevZoom + delta));
      const scaleFactor = newZoom / prevZoom;

      // Adjust pan so that the point under the mouse stays fixed
      setPan((prevPan) => ({
        x: mouseX - scaleFactor * (mouseX - prevPan.x),
        y: mouseY - scaleFactor * (mouseY - prevPan.y),
      }));

      return newZoom;
    });
  }, []);

  // Attach wheel with passive: false to allow preventDefault
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Pan via pointer (only when not placing a table)
  const handlePointerDown = useCallback(
    (e) => {
      // Only pan on direct canvas/viewport clicks (not on tables)
      const isCanvas = e.target === canvasRef.current || e.target === viewportRef.current;
      if (!isCanvas) return;
      if (placingTable) return;
      if (e.button !== 0) return;

      isPanning.current = false;
      panStart.current = { x: e.clientX, y: e.clientY };
      panOrigin.current = { ...pan };
      canvasRef.current.setPointerCapture(e.pointerId);

      const handlePanMove = (moveE) => {
        const dx = moveE.clientX - panStart.current.x;
        const dy = moveE.clientY - panStart.current.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
          isPanning.current = true;
        }
        if (!isPanning.current) return;
        setPan({
          x: panOrigin.current.x + dx,
          y: panOrigin.current.y + dy,
        });
      };

      const handlePanUp = (upE) => {
        canvasRef.current?.releasePointerCapture(upE.pointerId);
        canvas.removeEventListener('pointermove', handlePanMove);
        canvas.removeEventListener('pointerup', handlePanUp);
        // Reset isPanning after a tick so the click handler can check it
        requestAnimationFrame(() => {
          isPanning.current = false;
        });
      };

      const canvas = canvasRef.current;
      canvas.addEventListener('pointermove', handlePanMove);
      canvas.addEventListener('pointerup', handlePanUp);
    },
    [placingTable, pan]
  );

  // Reset zoom/pan
  const handleResetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
  }, []);

  const getGhostShapeClass = () => {
    switch (placingTable?.shape) {
      case 'round':
        return 'fp-table--round';
      case 'rect':
        return 'fp-table--rect';
      default:
        return 'fp-table--square';
    }
  };

  const isZoomed = zoom !== 1 || pan.x !== 0 || pan.y !== 0;

  return (
    <div
      ref={canvasRef}
      className={`fp-canvas ${placingTable ? 'fp-canvas--placing' : ''} ${!placingTable && zoom === 1 ? '' : ''}`}
      role="region"
      aria-label={t('tables.floorPlan', 'Plano del local')}
      onClick={handleCanvasClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onPointerDown={handlePointerDown}
      style={{ cursor: placingTable ? 'crosshair' : zoom > 1 || isZoomed ? 'grab' : undefined }}
    >
      <div
        ref={viewportRef}
        className="fp-canvas-viewport"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
        }}
      >
        {tables.length === 0 && !placingTable && (
          <div className="fp-canvas-empty">
            <span className="fp-canvas-empty-icon">📐</span>
          </div>
        )}
        {/* Group bounding boxes */}
        {(groups || []).map((group, idx) => {
          const groupTables = tables.filter((t) =>
            group.tableNumbers.includes(String(t.tableNumber))
          );
          if (groupTables.length < 2) return null;
          const xs = groupTables.map((t) => t.positionX);
          const ys = groupTables.map((t) => t.positionY);
          const pad = 5;
          const left = Math.max(0, Math.min(...xs) - pad);
          const top = Math.max(0, Math.min(...ys) - pad);
          const right = Math.min(100, Math.max(...xs) + pad);
          const bottom = Math.min(100, Math.max(...ys) + pad);
          const color = GROUP_COLORS[idx % GROUP_COLORS.length];
          return (
            <div
              key={group.groupId}
              className={`fp-group-boundary ${onGroupClick ? 'fp-group-boundary--clickable' : ''}`}
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${right - left}%`,
                height: `${bottom - top}%`,
                borderColor: color,
              }}
              onClick={(e) => {
                if (onGroupClick) {
                  e.stopPropagation();
                  onGroupClick(group);
                }
              }}
            >
              <span className="fp-group-label" style={{ color }}>
                {group.name}
              </span>
            </div>
          );
        })}
        {tables.map((table) => (
          <FloorPlanTable
            key={table.tableNumber}
            table={table}
            onDragEnd={onTableDragEnd}
            onContextMenu={onTableContextMenu}
            isSelected={selectedTableNumbers?.has(String(table.tableNumber))}
            selectionMode={selectionMode}
            onSelect={onTableSelect}
          />
        ))}
        {/* Ghost preview while placing */}
        {placingTable && ghostPos && (
          <div
            className={`fp-table fp-ghost ${getGhostShapeClass()}`}
            style={{
              left: `${ghostPos.x}%`,
              top: `${ghostPos.y}%`,
              transform: `translate(-50%, -50%) scale(${getTableScale(placingTable.seats)})`,
            }}
          >
            <span className="fp-table-number">{placingTable.tableNumber}</span>
          </div>
        )}
      </div>

      {/* Zoom controls */}
      <div className="fp-zoom-controls">
        <button
          type="button"
          className="fp-zoom-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleZoomIn();
          }}
          title={t('tables.zoomIn', 'Acercar')}
          aria-label={t('tables.zoomIn', 'Acercar')}
        >
          +
        </button>
        <span className="fp-zoom-level">{Math.round(zoom * 100)}%</span>
        <button
          type="button"
          className="fp-zoom-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleZoomOut();
          }}
          title={t('tables.zoomOut', 'Alejar')}
          aria-label={t('tables.zoomOut', 'Alejar')}
        >
          −
        </button>
        {isZoomed && (
          <button
            type="button"
            className="fp-zoom-btn fp-zoom-btn--reset"
            onClick={(e) => {
              e.stopPropagation();
              handleResetView();
            }}
            title={t('tables.resetView', 'Restablecer vista')}
            aria-label={t('tables.resetView', 'Restablecer vista')}
          >
            ⊡
          </button>
        )}
      </div>
    </div>
  );
}

export default FloorPlanCanvas;
